/**
 * Authentication context — reuses the Trac-IT JWT auth system.
 * Tokens are stored in localStorage for persistence across sessions.
 * Uses 7-day token lifetime for offline scenarios (backend must be configured).
 */

import type React from 'react';
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../services/apiClient';
import { AxiosError } from 'axios';

const AUTH_LOGIN = '/api/v1/auth/login';
const AUTH_ME = '/api/v1/auth/me';
const AUTH_LOGOUT = '/api/v1/auth/logout';
const AUTH_REFRESH = '/api/v1/auth/refresh';
const AUTH_CHANGE_PASSWORD = '/api/v1/auth/change-password';
const AUTH_ACCEPT_INVITE = '/api/v1/auth/accept-invite';
const CACHED_USER_KEY = 'cached_user';

// Matches the Trac-IT V2 employee auth contract (EmployeeUserInfo):
//   GET/POST /api/v1/auth/me|login -> { id, name, username, role, department, permissions }
// NOTE: V2 returns a single `name` and a single `role` (not `full_name`/`roles[]`),
// and does not return an email field.
interface User {
  id: number;
  name: string;
  username: string;
  role: string;
  department?: string | null;
  permissions?: string[];
  // VPS catalogue accounts flag a forced password change on first login / after reset.
  must_change_password?: boolean;
}

interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user: User;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  mustChangePassword: boolean;
  login: (username: string, password: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  acceptInvite: (inviteToken: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function getCachedUser(): User | null {
  try {
    const raw = localStorage.getItem(CACHED_USER_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch { return null; }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const fetchCurrentUser = useCallback(async (): Promise<User | null> => {
    try {
      const response = await apiClient.get<User>(AUTH_ME);
      const u = response.data;
      localStorage.setItem(CACHED_USER_KEY, JSON.stringify(u));
      setUser(u);
      return u;
    } catch (err) {
      const isNetworkError = err instanceof AxiosError && !err.response;
      if (isNetworkError) {
        // Offline — fall back to cached user profile instead of wiping auth
        const cached = getCachedUser();
        if (cached) {
          setUser(cached);
          return cached;
        }
      }
      // 401 or no cached user — clear auth
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem(CACHED_USER_KEY);
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      fetchCurrentUser().finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, [fetchCurrentUser]);

  const login = async (username: string, password: string): Promise<void> => {
    // Support both backend login contracts from a single build:
    //   - Trac-IT V2 backend expects a JSON body { username, password }.
    //   - The legacy one-port backend that serves this PWA over the LAN expects an
    //     OAuth2 form-encoded body (application/x-www-form-urlencoded).
    // Try JSON first; fall back to form-encoded ONLY when the body shape/media type is
    // rejected (422/415). A 401 (bad credentials) must propagate as-is — never retry it,
    // so a wrong password yields a single clean 401 with no follow-on auth churn.
    let payload: LoginResponse;
    try {
      const response = await apiClient.post<LoginResponse>(AUTH_LOGIN, { username, password });
      payload = response.data;
    } catch (err) {
      const status = err instanceof AxiosError ? err.response?.status : undefined;
      if (status !== 422 && status !== 415) throw err;
      const form = new URLSearchParams();
      form.append('username', username);
      form.append('password', password);
      const response = await apiClient.post<LoginResponse>(AUTH_LOGIN, form, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      payload = response.data;
    }

    applySession(payload);
    navigate(payload.user?.must_change_password ? '/change-password' : '/');
  };

  // Persist tokens + user from a login / accept-invite response.
  function applySession(payload: LoginResponse): void {
    const { access_token, refresh_token, user: loggedInUser } = payload;
    localStorage.setItem('auth_token', access_token);
    // Refresh relies on the httpOnly cookie; the body token is kept only as a
    // best-effort fallback for non-browser clients. The cookie is the source of truth.
    if (refresh_token) localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem(CACHED_USER_KEY, JSON.stringify(loggedInUser));
    setUser(loggedInUser);
  }

  const changePassword = async (currentPassword: string, newPassword: string): Promise<void> => {
    await apiClient.post(AUTH_CHANGE_PASSWORD, {
      current_password: currentPassword,
      new_password: newPassword,
    });
    // Password set — clear the forced-change flag locally + in cache.
    setUser(prev => {
      if (!prev) return prev;
      const next = { ...prev, must_change_password: false };
      localStorage.setItem(CACHED_USER_KEY, JSON.stringify(next));
      return next;
    });
  };

  const acceptInvite = async (inviteToken: string, password: string): Promise<void> => {
    const response = await apiClient.post<LoginResponse>(AUTH_ACCEPT_INVITE, {
      invite_token: inviteToken,
      password,
    });
    applySession(response.data);
    navigate('/');
  };

  const logout = (): void => {
    apiClient.post(AUTH_LOGOUT).catch(() => {});
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem(CACHED_USER_KEY);
    setUser(null);
    navigate('/login');
  };

  // Attempt token refresh on 401 (best-effort, non-blocking).
  // Network errors (offline) are ignored — don't force logout when there's no connection.
  useEffect(() => {
    const id = apiClient.interceptors.response.use(
      r => r,
      async error => {
        if (!error?.response) return Promise.reject(error); // network error — skip
        if (error.response.status === 401) {
          // Never run the refresh/logout machinery for the auth endpoints themselves.
          // A failed /login (wrong password) or a /logout must NOT trigger a refresh →
          // logout → /logout 401 → refresh → … loop. Likewise the refresh call's own
          // 401 must short-circuit. The cookie (withCredentials) carries the refresh
          // token, never an Authorization header.
          const url: string = error.config?.url ?? '';
          const isAuthCall =
            url.includes(AUTH_LOGIN) ||
            url.includes(AUTH_LOGOUT) ||
            url.includes(AUTH_REFRESH);
          // No active session → nothing to refresh and no logout to spam.
          const hasSession = !!localStorage.getItem('auth_token');
          if (isAuthCall || !hasSession) {
            return Promise.reject(error);
          }
          if (error.config && !error.config._retried) {
            try {
              const res = await apiClient.post<{ access_token: string }>(AUTH_REFRESH);
              localStorage.setItem('auth_token', res.data.access_token);
              error.config._retried = true;
              error.config.headers.Authorization = `Bearer ${res.data.access_token}`;
              return apiClient(error.config);
            } catch {
              logout();
            }
          } else {
            logout();
          }
        }
        return Promise.reject(error);
      }
    );
    return () => apiClient.interceptors.response.eject(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        mustChangePassword: !!user?.must_change_password,
        login,
        changePassword,
        acceptInvite,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
